/**
 * ============================================================================
 * Copy Link Manager - Vue 组件化版本（纯渲染入口）
 * ============================================================================
 * 
 * 【架构说明】
 * 
 * 本模块采用 Vue 3 + Options API + EventBus 实现完全解耦的组件化架构。
 * 核心设计原则：
 * 1. copy-link.js 仅负责扫描 DOM 和挂载 Vue 组件（纯渲染器）
 * 2. 所有状态和交互都在 Vue 组件内部管理
 * 3. 通过 EventBus 实现组件间松耦合通信
 * 
 * ----------------------------------------------------------------------------
 * 【技术栈】
 * ----------------------------------------------------------------------------
 * - Vue 3 (Options API)
 * - Rspack + rspack-vue-loader
 * - ES6 Modules
 * - EventBus (自定义事件总线)
 * 
 * ----------------------------------------------------------------------------
 * 【组件结构】
 * ----------------------------------------------------------------------------
 * 
 * 1. CopyLinkButton.vue
 *    位置: ../components/CopyLinkButton.vue
 *    职责:
 *    - 渲染复制链接按钮（SVG 图标）
 *    - 处理 hover/press 交互状态
 *    - 点击时复制完整 URL 到剪贴板
 *    - 通过 EventBus 发送 'copy' 事件
 *    
 *    Props:
 *    - nameText (String, required): 标题文本
 *    - anchorId (String, required): 锚点 ID
 *    - href (String, default: '#'): 链接地址
 * 
 * 2. ClipboardNotification.vue
 *    位置: ../components/ClipboardNotification.vue
 *    职责:
 *    - 监听 EventBus 的 'copy' 事件
 *    - 显示“已复制到剪贴板”通知卡片
 *    - 自动隐藏（3秒后）
 *    - 提供“查看”和“关闭”按钮
 *    
 *    内部状态:
 *    - visible (Boolean): 是否显示通知
 *    - url (String): 复制的 URL
 * 
 * 3. EventBus
 *    位置: ../utils/event-bus.js
 *    职责:
 *    - 提供 copyLinkBus 实例
 *    - 实现组件间松耦合通信
 *    - 事件: 'copy(url)'
 * 
 * ----------------------------------------------------------------------------
 * 【工作流程】
 * ----------------------------------------------------------------------------
 * 
 * 1. DOM 扫描阶段 (scanAndMountButtons)
 *    - 查找所有 .anchor-heading 元素
 *    - 提取 .name-text 内容和生成锚点 ID
 *    - 为每个标题创建 Vue 挂载点
 *    - 实例化 CopyLinkButton 并挂载
 * 
 * 2. 通知初始化阶段 (initNotification)
 *    - 查找或创建 .page-clipboard-com 容器
 *    - 实例化 ClipboardNotification（单例模式）
 *    - 组件内部监听 EventBus
 * 
 * 3. 事件流
 *    用户点击复制按钮
 *      ↓
 *    CopyLinkButton.copyToClipboard()
 *      ↓
 *    copyLinkBus.emit('copy', url)
 *      ↓
 *    ClipboardNotification 监听到事件
 *      ↓
 *    显示通知，3秒后自动隐藏
 * 
 * 4. 锚点滚动阶段 (initScrollToHash)
 *    - 检测 URL hash
 *    - 解码并定位目标元素
 *    - 考虑 scroll-margin-top 执行平滑滚动
 * 
 * ----------------------------------------------------------------------------
 * 【DOM 结构转换】
 * ----------------------------------------------------------------------------
 * 
 * 输入（WordPress Gutenberg 生成）:
 * <h3 class="wp-block-heading ambassador-name anchor-heading">
 *   <span class="name-text">Rico Cheung</span>
 * </h3>
 * 
 * 输出（Vue 增强后）:
 * <h3 class="wp-block-heading ambassador-name anchor-heading">
 *   <div class="vue-copy-link-mount">
 *     <!-- CopyLinkButton 组件渲染完整结构 -->
 *     <div class="copy-link-box">
 *       <div class="copy-link-wrap">
 *         <a class="copy-link" href="#h.rico_cheung">
 *           <svg>...</svg>
 *         </a>
 *       </div>
 *       <span class="name-text">Rico Cheung</span>  <!-- Vue 渲染 -->
 *     </div>
 *   </div>
 * </h3>
 * 
 * 注意:
 * - Vue 组件直接渲染 .name-text，无需外部传入
 * - heading.innerHTML 被清空，由 Vue 完全接管
 * - CSS 类名保持与原有样式兼容
 * 
 * ----------------------------------------------------------------------------
 * 【关键函数说明】
 * ----------------------------------------------------------------------------
 * 
 * generateAnchorId(nameText)
 *   功能: 生成唯一锚点 ID
 *   算法: 'h.' + 小写 + 空格转下划线 + 移除非字母数字字符
 *   支持: Unicode 字母（中文、日文等）
 *   示例: "Rico Cheung" → "h.rico_cheung"
 * 
 * scanAndMountButtons()
 *   功能: 扫描页面并挂载 CopyLinkButton 组件
 *   流程: 查找 heading → 提取数据 → 创建挂载点 → 实例化 Vue → 插入 DOM
 *   防护: 避免重复挂载（检查 .vue-copy-link-mount 是否存在）
 * 
 * initNotification()
 *   功能: 初始化 ClipboardNotification 组件（单例）
 *   特点: 组件内部通过 EventBus 自行管理状态
 * 
 * initScrollToHash()
 *   功能: URL hash 自动滚动定位
 *   时机: DOMContentLoaded 后使用 requestAnimationFrame
 *   API: scrollIntoView({ behavior: 'smooth' })
 *   转义: 使用 CSS.escape 处理特殊字符（如 h.rico_cheung 中的 .）
 *   优势: 现代浏览器原生平滑滚动，自动处理 ID 转义
 * 
 * ----------------------------------------------------------------------------
 * 【兼容性处理】
 * ----------------------------------------------------------------------------
 * 
 * 1. 剪贴板 API 降级
 *    - 现代浏览器: navigator.clipboard.writeText()
 *    - 旧版浏览器: document.execCommand('copy')
 *    - 失败处理: console.warn/error 记录日志
 * 
 * 2. 触摸设备支持
 *    - touchstart/touchend/touchcancel 事件
 *    - 与 mousedown/mouseup 相同的 press 状态管理
 * 
 * 3. URL 编码处理
 *    - decodeURIComponent 解码 hash
 *    - 支持中文字符等特殊字符
 * 
 * ----------------------------------------------------------------------------
 * 【性能优化】
 * ----------------------------------------------------------------------------
 * 
 * 1. 避免重复挂载
 *    - 检查 .vue-copy-link-mount 类名
 *    - 已挂载的标题跳过处理
 * 
 * 2. 单例通知组件
 *    - 全局只有一个 ClipboardNotification 实例
 *    - 组件内部管理状态，无需外部控制
 * 
 * 3. 事件监听清理
 *    - beforeUnmount 钩子移除 EventBus 监听
 *    - 清除 hideTimer 定时器
 *    - 避免内存泄漏
 * 
 * ----------------------------------------------------------------------------
 * 【使用方式】
 * ----------------------------------------------------------------------------
 * 
 * 自动初始化:
 *   模块加载后自动执行 initCopyLinks()
 *   无需手动调用
 * 
 * 手动控制（如需要）:
 *   import { initCopyLinks } from './page/copy-link';
 *   initCopyLinks();
 * 
 * ----------------------------------------------------------------------------
 * 【调试信息】
 * ----------------------------------------------------------------------------
 * 
 * Console 日志:
 * - [CopyLinkManager] Initialized successfully
 * - [CopyLinkManager] Container not found, creating one
 * - [CopyLinkButton] Clipboard API failed: ...
 * - [CopyLinkButton] execCommand copy failed: ...
 * 
 * 检查挂载状态:
 *   document.querySelectorAll('.vue-copy-link-mount').length
 * 
 * ============================================================================
 */

import { createApp } from 'vue';
import CopyLinkButton from '../components/CopyLinkButton.vue';
import ClipboardNotification from '../components/ClipboardNotification.vue';

/**
 * 生成唯一锚点 ID
 * 支持所有 Unicode 字母（包括中文、日文等）
 * @param {string} nameText - 标题文本
 * @returns {string} 锚点 ID
 */
function generateAnchorId(nameText) {
  return 'h.' + nameText
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^\p{L}\p{N}_]/gu, '');
}

/**
 * 扫描页面并挂载 CopyLinkButton 组件
 */
function scanAndMountButtons() {
  const headings = document.querySelectorAll('.anchor-heading');
  
  headings.forEach((heading) => {
    // 避免重复挂载
    if (heading.querySelector('.vue-copy-link-mount')) {
      return;
    }

    const nameSpan = heading.querySelector('.name-text');
    if (!nameSpan) return;

    // 提取数据
    const nameText = nameSpan.textContent || '';
    const anchorId = generateAnchorId(nameText);

    // 设置锚点 ID（优先在 .anchor-section 上，降级到 heading）
    const card = heading.closest('.anchor-section');
    if (card) {
      card.id = anchorId;
    } else {
      heading.id = anchorId;
    }

    // 清空 heading 内容
    heading.innerHTML = '';

    // 创建 Vue 挂载点
    const mountPoint = document.createElement('div');
    mountPoint.className = 'vue-copy-link-mount';
    heading.appendChild(mountPoint);

    // 创建并挂载 Vue 实例（Vue 组件渲染完整结构，包括 .name-text）
    createApp(CopyLinkButton, {
      nameText,
      anchorId,
      href: '#' + anchorId
    }).mount(mountPoint);
  });

  console.log(`[CopyLinkManager] Mounted ${headings.length} copy link buttons`);
}

/**
 * 初始化通知组件（单例模式）
 */
function initNotification() {
  // 查找或创建容器
  let container = document.querySelector('.page-clipboard-com');
  if (!container) {
    console.log('[CopyLinkManager] Container .page-clipboard-com not found, creating one');
    container = document.createElement('div');
    container.className = 'page-clipboard-com';
    document.body.appendChild(container);
  }

  // 检查是否已存在通知组件
  if (container.querySelector('.vue-notification-mount')) {
    console.log('[CopyLinkManager] Notification already initialized');
    return;
  }

  // 创建挂载点
  const mountPoint = document.createElement('div');
  mountPoint.className = 'vue-notification-mount';
  container.appendChild(mountPoint);

  // 创建 Vue 实例（组件内部自行管理状态和监听 EventBus）
  createApp(ClipboardNotification).mount(mountPoint);

  console.log('[CopyLinkManager] Notification component initialized');
}

/**
 * 初始化锚点滚动（使用现代平滑滚动 API）
 */
function initScrollToHash() {
  const hash = window.location.hash;
  if (!hash) return;

  // 使用 requestAnimationFrame 确保 DOM 完全渲染
  requestAnimationFrame(() => {
    // 1. 解码 URL 编码的 hash（处理中文字符等）
    let decodedHash;
    try {
      decodedHash = decodeURIComponent(hash);
    } catch (e) {
      // 如果解码失败，使用原始 hash
      decodedHash = hash;
    }
    
    // 2. 确保以 # 开头
    const selector = decodedHash.startsWith('#') ? decodedHash : '#' + decodedHash;
    
    // 3. 使用 CSS.escape 转义特殊字符（如 h.rico_cheung 中的 .）
    let target;
    try {
      // 提取 ID 部分（去掉 #）并转义
      const idPart = selector.slice(1);
      const escapedId = CSS.escape(idPart);
      const finalSelector = '#' + escapedId;
      target = document.querySelector(finalSelector);
    } catch (e) {
      // 降级方案：直接查询（适用于简单 ID）
      target = document.querySelector(selector);
    }
    
    if (!target) {
      console.warn(`[CopyLinkManager] Target not found for hash: ${selector}`);
      return;
    }

    // 4. 使用现代 scrollIntoView API，支持平滑滚动
    target.scrollIntoView({
      behavior: 'smooth',  // 平滑滚动
      block: 'start',      // 滚动到元素顶部
      inline: 'nearest'
    });
    
    console.log(`[CopyLinkManager] Smooth scrolled to: ${selector}`);
  });
}

/**
 * 初始化复制链接功能
 */
export function initCopyLinks() {
  console.log('[CopyLinkManager] Starting initialization...');

  // 1. 初始化通知组件
  initNotification();

  // 2. 扫描并挂载按钮组件
  scanAndMountButtons();

  // 3. 初始化锚点滚动
  initScrollToHash();

  console.log('[CopyLinkManager] Initialized successfully');
}

// 自动初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCopyLinks();
  });
} else {
  initCopyLinks();
}
